import { useFetchHomeGroups } from "src/data/homeGroups/queries/homeGroupQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchHomes } from "src/data/organizations/queries/homeQueries"
import { useFetchMembers } from "src/data/organizations/queries/memberQueries"
import { useFetchTasks } from "src/data/taskManagement/taskManagementQueries"
import {
  TTask,
  TTaskFetchFilters,
} from "src/data/taskManagement/taskManagementTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { unique } from "src/utils/genericUtil"

// This will eventually come from the backend in GXP-1131
export function useTaskTypeLabels(): Record<TTask["category"], string> {
  const { tPlaceholder } = useTranslate()

  return {
    maintenance: tPlaceholder("Maintenance"),
    cleaning: tPlaceholder("Cleaning"),
    other: tPlaceholder("Other"),
  }
}

export function useFetchTaskTableData(filters: TTaskFetchFilters) {
  const { orgId } = useOrganization()

  const fetchTasks = useFetchTasks({
    orgId,
    filters,
  })

  const tasks = fetchTasks.data?.tasks ?? []

  const homeIds = tasks
    .map((task) => task.home_id)
    .filter((id): id is string => typeof id === "string")

  const fetchHomes = useFetchHomes({
    orgId,
    filters: { ids: homeIds },
  })

  const homeOptions = fetchHomes.data?.homes ?? []

  const fetchMembers = useFetchMembers({ orgId })
  const members = fetchMembers.data?.members ?? []
  const memberOptions = members.map((m) => ({
    id: m.member_id,
    name: m.name,
  }))

  const uniqueHomeGroupIds = unique(homeOptions.map((t) => t.homegroup_id))
    .filter(Boolean)
    .join(",")

  const fetchHomeGroups = useFetchHomeGroups({
    orgId,
    filter: { ids: uniqueHomeGroupIds },
    options: {
      // The backend expects a comma separated string of ids each with a minimum length of 24
      enabled: uniqueHomeGroupIds.length >= 24,
    },
  })

  const homeGroupOptions = fetchHomeGroups.data?.homegroups ?? []

  const loading =
    fetchTasks.isFetching ||
    fetchHomes.isFetching ||
    fetchMembers.isFetching ||
    fetchHomeGroups.isFetching

  return {
    loading,
    tasks,
    homes: homeOptions,
    members: memberOptions,
    homeGroups: homeGroupOptions,
    pagingTotalCount: fetchTasks.data?.paging.total_count ?? 0,
  }
}
